import type { ProtectionState } from '../types';
import { swapBlur } from './blur-overlay';

let debugMode = false;

export function setTouchHandlerDebug(enabled: boolean): void {
    debugMode = enabled;
}

/**
 * Attaches touch event handlers to a protected element.
 * In absolute mode (detail view) the container receives events.
 * In sibling mode (grid view) we listen on the parent element of the original
 * img.  The blur clones sit above the img visually but React photo overlays
 * (hover effects, checkboxes, etc.) may be above the clones in z-index,
 * making the clones unreliable as touch targets.  The parent element receives
 * all bubbled touch events regardless of which child is on top.
 */
export function attachTouchHandlers(state: ProtectionState): void {
    const { container } = state;

    // Absolute mode: use the container.
    // Sibling mode: use the parent of the original element.
    const targets: HTMLElement[] = container
        ? [container]
        : (() => {
              const parent = (state.element as HTMLElement).parentElement;
              return parent ? [parent] : [];
          })();

    if (targets.length === 0) {
        return;
    }

    /** Minimum press duration (ms) before we swap blur; shorter taps are ignored so click can open photo view */
    const LONG_PRESS_MS = 400;
    let pressTimer: ReturnType<typeof setTimeout> | null = null;
    let didLongPress = false;

    const clearPressTimer = (reason: string): void => {
        if (pressTimer !== null) {
            if (debugMode) {
                console.log('[CopyDeter] clearPressTimer:', reason);
            }

            clearTimeout(pressTimer);
            pressTimer = null;
        }
    };

    const touchStartHandler = (e: Event): void => {
        if (debugMode) {
            console.log('[CopyDeter] touchStartHandler triggered by', e.type);
        }

        clearPressTimer('new touchstart');
        didLongPress = false;
        pressTimer = setTimeout(() => {
            pressTimer = null;
            didLongPress = true;

            if (debugMode) {
                console.log('[CopyDeter] long-press timer fired, calling swapBlur');
            }

            swapBlur(state, debugMode);
        }, LONG_PRESS_MS);
    };

    const touchEndHandler = (e: Event): void => {
        if (debugMode) {
            console.log('[CopyDeter] touchEndHandler triggered by', e.type, { didLongPress });
        }

        clearPressTimer('touchend');

        if (didLongPress) {
            didLongPress = false;
            return;
        }

        // Short tap: forward click so the photo opens normally.
        // Absolute mode: click the next sibling (e.g. gallery PhotoOverlay).
        // Sibling mode: we're listening on the parent element, so the native
        // touch sequence already fires a synthetic click on the tapped child —
        // no forwarding needed.
        if (container) {
            const next = container.nextElementSibling as HTMLElement | null;

            if (next && typeof next.click === 'function') {
                next.click();
            }
        }
    };

    const cancelHandler = (e: Event): void => {
        if (debugMode) {
            console.log('[CopyDeter] cancelHandler triggered by', e.type);
        }

        clearPressTimer('cancel');
        didLongPress = false;
    };

    state.touchStartHandler = touchStartHandler;
    state.touchEndHandler = touchEndHandler;
    state.touchCancelHandler = cancelHandler;

    for (const target of targets) {
        target.addEventListener('touchstart', touchStartHandler, { passive: true });
        target.addEventListener('touchend', touchEndHandler, { passive: true });
        target.addEventListener('touchcancel', cancelHandler, { passive: true });

        target.addEventListener('mousedown', touchStartHandler);
        target.addEventListener('mouseup', touchEndHandler);
        target.addEventListener('mouseleave', cancelHandler);
    }
}

/**
 * Removes touch event handlers from a protected element.
 */
export function detachTouchHandlers(state: ProtectionState): void {
    const { container, touchStartHandler, touchEndHandler, touchCancelHandler } = state;

    const targets: HTMLElement[] = container
        ? [container]
        : (() => {
              const parent = (state.element as HTMLElement).parentElement;
              return parent ? [parent] : [];
          })();

    if (targets.length === 0) {
        return;
    }

    for (const target of targets) {
        if (touchStartHandler) {
            target.removeEventListener('touchstart', touchStartHandler);
            target.removeEventListener('mousedown', touchStartHandler);
        }

        if (touchEndHandler) {
            target.removeEventListener('touchend', touchEndHandler);
            target.removeEventListener('mouseup', touchEndHandler);
        }

        if (touchCancelHandler) {
            target.removeEventListener('touchcancel', touchCancelHandler);
            target.removeEventListener('mouseleave', touchCancelHandler);
        }
    }

    state.touchStartHandler = undefined;
    state.touchEndHandler = undefined;
    state.touchCancelHandler = undefined;
}
